import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
    CommunityOrgTypeSentinel,
    EVERYONE_SENTINEL,
    ReferralTypesOptions,
} from "@sp-crm/core";
import { countFiltersApplied } from "components/advanced-search/advanced-search-utility";
import { SearchGeoParamsCallout } from "components/community-search/search-bar/search-geo-params-callout";
import { SearchbarItemBadge } from "components/community-search/search-bar/searchbar-item-badge";
import { useDebouncedState } from "components/shared/hooks";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { Callout } from "components/ui/callout";
import { Checkbox } from "components/ui/checkbox";
import { SearchInput } from "components/ui/search-input";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    AdvancedSearchCondition,
    LayoutSectionParentKey,
    SearchGeoParams,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCanSeeReferralSourceOwners } from "store/selectors/hooks";
import {
    ReferenceDashboardAspect,
    ReferenceFilter,
} from "store/slices/reference-dashboard";
import { EntityMoreFilter, EntityMoreFilterValue } from "./entity-more-filter";
import { ReferenceSearchSort, ReferenceSearchSortValues } from "./reference-search-sort";

interface ReferenceSearchBarProps {
    aspect?: ReferenceDashboardAspect;
    value: ReferenceFilter;
    onChange: (filter: ReferenceFilter) => void;
    sortValues?: ReferenceSearchSortValues;
    onSortChange?: (
        aspect: ReferenceDashboardAspect,
        sort: string,
        sortDirection: "ASC" | "DESC",
    ) => void;
    onResetMapView?: () => void;
}

export const ReferenceSearchBar: React.FC<ReferenceSearchBarProps> = props => {
    const assignedToVisible = useCanSeeReferralSourceOwners();
    const { aspect, value, onChange, onSortChange, sortValues, onResetMapView } = props;

    const [searchTerm, setSearchTerm, searchTermDebounced] = useDebouncedState(
        value.search,
        400,
    );

    useEffect(() => {
        if (searchTermDebounced !== value.search) {
            onChange({
                ...value,
                search: searchTermDebounced,
            });
        }
    }, [searchTermDebounced]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOrgTypeChanged = (orgTypes: string[]) => {
        onChange({
            ...value,
            orgTypes,
        });
    };

    const handleAssignedUserChanged = (assignedUserId: SelectableUser) => {
        onChange({
            ...value,
            assignedUserId,
        });
    };

    const handleContactConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                contactCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleBusinessConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                businessCondition: condition,
            });
        },
        [onChange, value],
    );

    const handleCommunityConditionChanged = useCallback(
        (condition: AdvancedSearchCondition | null) => {
            onChange({
                ...value,
                communityCondition: condition,
            });
        },
        [onChange, value],
    );

    const showClearButton = useMemo(() => {
        return (
            value.search.length > 0 ||
            value.orgTypes.length > 0 ||
            countFiltersApplied(value.contactCondition) > 0 ||
            countFiltersApplied(value.businessCondition) > 0 ||
            countFiltersApplied(value.communityCondition) > 0 ||
            value.assignedUserId !== EVERYONE_SENTINEL
        );
    }, [
        value.assignedUserId,
        value.search.length,
        value.orgTypes.length,
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
    ]);

    const handleGeoParamsChanged = (geoParams: SearchGeoParams | null) => {
        onChange({
            ...value,
            geoParams,
        });
    };

    const handleEntityConditionChanged = useCallback(
        (entityType: string, condition: AdvancedSearchCondition | null) => {
            if (entityType === "ReferenceContact") {
                handleContactConditionChanged(condition);
            } else if (entityType === "ReferenceBusiness") {
                handleBusinessConditionChanged(condition);
            } else if (entityType === "Community") {
                handleCommunityConditionChanged(condition);
            }
        },
        [
            handleBusinessConditionChanged,
            handleCommunityConditionChanged,
            handleContactConditionChanged,
        ],
    );

    const conditionConfigs: Record<string, EntityMoreFilterValue> = useMemo(() => {
        return {
            ReferenceContact: {
                condition: value.contactCondition,
                onChange: handleContactConditionChanged,
                label: "Contact filters",
                icon: "40_ReferralPerson",
                entityType: "ReferenceContact",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceContact],
            },
            ReferenceBusiness: {
                condition: value.businessCondition,
                onChange: handleBusinessConditionChanged,
                label: "Referral Organization filters",
                icon: "39_Business",
                entityType: "ReferenceBusiness",
                layoutContainerKeys: [LayoutSectionParentKey.ReferenceBusiness],
            },
            Community: {
                condition: value.communityCondition,
                onChange: handleCommunityConditionChanged,
                label: "Community filters",
                icon: "57_Community",
                entityType: "Community",
                layoutContainerKeys: [
                    LayoutSectionParentKey.CommunityDetailsColumn1,
                    LayoutSectionParentKey.CommunityDetailsColumn2,
                ],
            },
        };
    }, [
        value.contactCondition,
        value.businessCondition,
        value.communityCondition,
        handleBusinessConditionChanged,
        handleCommunityConditionChanged,
        handleContactConditionChanged,
    ]);

    return (
        <div className="flex items-end space-x-4 flex-wrap">
            <div className="flex-1 min-w-32 max-w-lg">
                <SearchInput
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Organization name or contact name"
                />
            </div>
            <UserSelect
                label="Filter by assigned to"
                onChange={handleAssignedUserChanged}
                value={value.assignedUserId}
                includeEveryone
                restricted={!assignedToVisible}
            />
            <div className="flex items-center space-x-4 pb-2">
                {onResetMapView ? (
                    <SearchGeoParamsCallout
                        value={value.geoParams}
                        onChange={handleGeoParamsChanged}
                        onResetMapView={onResetMapView}
                    />
                ) : null}
                <OrgTypeFilter value={value.orgTypes} onChange={handleOrgTypeChanged} />
                <EntityMoreFilter
                    conditions={[conditionConfigs.ReferenceContact]}
                    onConditionChange={handleEntityConditionChanged}
                    label="Contact filters"
                />
                <EntityMoreFilter
                    conditions={[
                        conditionConfigs.ReferenceBusiness,
                        conditionConfigs.Community,
                    ]}
                    onConditionChange={handleEntityConditionChanged}
                    label="Organization filters"
                />
                {aspect && sortValues && onSortChange ? (
                    <ReferenceSearchSort
                        aspect={aspect}
                        values={sortValues}
                        onChange={onSortChange}
                    />
                ) : null}
                {showClearButton ? (
                    <a
                        href="#"
                        onClick={e => {
                            e.preventDefault();
                            setSearchTerm("");
                            onChange({
                                search: "",
                                orgTypes: [],
                                contactCondition: null,
                                businessCondition: null,
                                communityCondition: null,
                                assignedUserId: EVERYONE_SENTINEL,
                                geoParams: null,
                            });
                        }}>
                        Clear all filters
                    </a>
                ) : null}
            </div>
        </div>
    );
};

interface OrgTypeFilterProps {
    value: string[];
    onChange: (orgTypes: string[]) => void;
}

const OrgTypeFilter: React.FC<OrgTypeFilterProps> = props => {
    const { value, onChange } = props;

    const [itemFilterText, setItemFilterText] = useState("");

    const renderButton = useCallback(() => {
        return (
            <div className="flex items-center space-x-1 cursor-pointer text-gray-700">
                <div className="text-lg">Organization type</div>
                {value.length > 0 ? <SearchbarItemBadge counter={value.length} /> : null}
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            </div>
        );
    }, [value.length]);

    const addOrgType = (orgType: string) => {
        if (!value.includes(orgType)) {
            const newOrgTypes = [...value, orgType];
            onChange(newOrgTypes);
        }
    };

    const removeOrgType = (orgType: string) => {
        if (value.includes(orgType)) {
            const newOrgTypes = value.filter(ot => ot !== orgType);
            onChange(newOrgTypes);
        }
    };

    const clearOrgTypes = useCallback(() => {
        onChange([]);
    }, [onChange]);

    const headerGroups = useMemo(() => {
        const communityOption = {
            header: "Record Type",
            groupOptions: [
                {
                    text: "Community",
                    value: CommunityOrgTypeSentinel,
                },
            ],
        };

        return [...ReferralTypesOptions, communityOption]
            .map(o => {
                return {
                    header: o.header,
                    groupOptions: o.groupOptions.filter(
                        g =>
                            o.header
                                .toLowerCase()
                                .includes(itemFilterText.toLowerCase()) ||
                            g.text.toLowerCase().includes(itemFilterText.toLowerCase()),
                    ),
                };
            })
            .filter(o => o.groupOptions.length > 0);
    }, [itemFilterText]);

    return (
        <Callout renderButton={renderButton}>
            <div className="space-y-2">
                <SecondaryButton
                    onClick={e => {
                        e.preventDefault();
                        clearOrgTypes();
                    }}>
                    <span className="text-sm">Clear</span>
                </SecondaryButton>
                <SearchInput
                    value={itemFilterText}
                    onChange={e => {
                        setItemFilterText(e.target.value);
                    }}
                />
                <ul className="w-80">
                    {headerGroups.map((option, index) => (
                        <li key={index}>
                            <p className="font-bold">{option.header}</p>
                            <ul>
                                {option.groupOptions.map(
                                    (groupOption, groupOptionIndex) => (
                                        <li key={groupOptionIndex}>
                                            <Checkbox
                                                checked={value.includes(
                                                    groupOption.value,
                                                )}
                                                onChange={e => {
                                                    e.target.checked
                                                        ? addOrgType(groupOption.value)
                                                        : removeOrgType(
                                                              groupOption.value,
                                                          );
                                                }}
                                                label={groupOption.text}
                                            />
                                        </li>
                                    ),
                                )}
                            </ul>
                        </li>
                    ))}
                </ul>
            </div>
        </Callout>
    );
};
